import classNames from 'classnames'
import { t } from 'i18next'
import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { PickList } from 'primereact/picklist'
import { SelectButton } from 'primereact/selectbutton'
import { Steps } from 'primereact/steps'
import { ToggleButton } from 'primereact/togglebutton'
import { Tree } from 'primereact/tree'
import TreeNode from 'primereact/treenode'
import React, { useEffect, useState } from 'react'
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { ModuleNodeTemplate } from './utils/ModuleNodeTemplate'
import {
  getSemesterArray,
  ModulePlanItemTemplate,
  ModuleTags,
} from './utils/ModulePlanHelper'
import {
  API_APPRENTICESHIP_MODULE_PLANS,
  API_APPRENTICESHIP_MODULES,
  API_APPRENTICESHIP_TYPES,
} from '../../../../../constants'
import { GradeSchema } from '../../../../../model/apprenticeModuleGrade.model'
import {
  ApprenticeshipModule,
  ApprenticeshipModuleInPlan,
  ApprenticeshipModulePlan,
  ApprenticeshipModuleWithSemesters,
} from '../../../../../model/apprenticeshipModule.model'
import { ApprenticeshipType } from '../../../../../model/ApprenticeshipType'
import { DropdownItem } from '../../../../../model/dropdownItem.model'
import {
  ApprenticeshipTypeCt,
  RequestMethod,
} from '../../../../../model/enums/enums'
import {
  addModules,
  removeModules,
} from '../../../../../redux/basic/grades/apprenticeship-module/moduleActions'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'
import { AuthRoutes } from '../../../../../router/routes'
import { handleRequest } from '../../../../../services/api/base/handleRequest'
import { BackButton } from '../../../../util/button/BackButton'
import { GroupMultiselect } from '../../../../util/form/dropdown/group-multiselect/GroupMultiselect'
import { RoundingTypeDropdown } from '../../util/RoundingTypeDropdown'
import { GradeSchemaDropdown } from '../utils/GradeSchemaDropdown'
import { ModuleKeyBadgeByType, ModuleTypeTag } from '../utils/ModuleTypeTag'

export const AddEducationPlan = () => {
  const allModules = useAppSelector((state) => state.apprenticeshipModules)
  const [allModulesWithoutSelected, setAllModulesWithoutSelected] = useState<
    ApprenticeshipModuleInPlan[]
  >([])
  const [selectedModules, setSelectedModules] = useState<
    ApprenticeshipModuleWithSemesters[]
  >([])

  const [apprenticeshipType, setApprenticeshipType] = useState<number>(
    ApprenticeshipTypeCt.efz
  )
  const [apprenticeshipTypes, setApprenticeshipTypes] = useState<
    ApprenticeshipType[]
  >([])
  const [
    apprenticeshipDurationInSemester,
    setApprenticeshipDurationInSemester,
  ] = useState<number | undefined>(8)
  const [semesterSelectOptions, setSemesterSelectOptions] = useState<{}[]>()
  const navigate = useNavigate()
  const dispatcher = useAppDispatch()

  const [activeIndex, setActiveIndex] = useState<number>(0)

  const stepItems = [
    {
      label: t<string>('grade_module.education_plan.setup_modules'),
    },
    {
      label: t<string>('core.review_result'),
    },
  ]

  const [semesterNodes, setSemesterNodes] = useState<TreeNode[]>([
    {
      key: 1,
      label: `. ${t<string>('core.education.semester')}`,
      draggable: false,
      icon: 'fa-solid fa-1',
      children: [],
      className: 'parent-tree-node',
    },
    {
      key: 2,
      label: `. ${t<string>('core.education.semester')}`,
      draggable: false,
      icon: 'fa-solid fa-2',
      children: [],
      className: 'parent-tree-node',
    },
    {
      key: 3,
      label: `. ${t<string>('core.education.semester')}`,
      draggable: false,
      icon: 'fa-solid fa-3',
      children: [],
      className: 'parent-tree-node',
    },
    {
      key: 4,
      label: `. ${t<string>('core.education.semester')}`,
      draggable: false,
      icon: 'fa-solid fa-4',
      children: [],
      className: 'parent-tree-node',
    },
    {
      key: 5,
      label: `. ${t<string>('core.education.semester')}`,
      draggable: false,
      icon: 'fa-solid fa-5',
      children: [],
      className: 'parent-tree-node',
    },
    {
      key: 6,
      label: `. ${t<string>('core.education.semester')}`,
      draggable: false,
      icon: 'fa-solid fa-6',
      children: [],
      className: 'parent-tree-node',
    },
    {
      key: 7,
      label: `. ${t<string>('core.education.semester')}`,
      draggable: false,
      icon: 'fa-solid fa-7',
      children: [],
      className: 'parent-tree-node',
    },
    {
      key: 8,
      label: `. ${t<string>('core.education.semester')}`,
      draggable: false,
      icon: 'fa-solid fa-8',
      children: [],
      className: 'parent-tree-node',
    },
  ])

  const [expandedKeys, setExpandedKeys] = useState({})
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const expandNode = (node: any, _expandedKeys: any) => {
    if (node.children && node.children.length) {
      _expandedKeys[node.key] = true

      for (let child of node.children) {
        expandNode(child, _expandedKeys)
      }
    }
  }

  const expandAll = () => {
    let _expandedKeys = {}
    for (let node of semesterNodes) {
      expandNode(node, _expandedKeys)
    }
    setExpandedKeys(_expandedKeys)
  }

  useEffect(() => {
    if (activeIndex === 1) {
      expandAll()
      setIsExpanded(true)
    } else {
      collapseAll()
      setIsExpanded(false)
    }
  }, [activeIndex])

  const collapseAll = () => {
    setExpandedKeys({})
  }

  const sortByKey = (key: string, modules: any[]) => {
    return modules?.sort(function (a: any, b: any) {
      // @ts-ignore
      let x = a[key]
      // @ts-ignore
      let y = b[key]
      return x < y ? -1 : x > y ? 1 : 0
    })
  }

  useEffect(() => {
    const loadAll = async () => {
      const mods = await handleRequest<ApprenticeshipModuleInPlan[]>(
        API_APPRENTICESHIP_MODULES(),
        RequestMethod.GET
      )
      if (mods.data && !mods.error) {
        // handling 2x loading by useState to prevent duplicates
        dispatcher(removeModules())
        dispatcher(addModules(mods.data))
      }
    }
    if (allModules.length <= 0) {
      loadAll()
    }
  }, [dispatcher])

  useEffect(() => {
    if (!apprenticeshipDurationInSemester) {
      return
    }

    const loadAllApprenticeshipTypes = async () => {
      if (apprenticeshipTypes.length <= 0) {
        const result = await handleRequest<ApprenticeshipType[]>(
          API_APPRENTICESHIP_TYPES(),
          RequestMethod.GET
        )
        if (result.data && !result.error) {
          setApprenticeshipTypes(result.data)
        }
      }
    }
    loadAllApprenticeshipTypes().then(() => {
      const sems = getSemesterArray(apprenticeshipDurationInSemester)
      setSemesterSelectOptions(sems)
    })
  }, [apprenticeshipDurationInSemester, apprenticeshipType])

  useEffect(() => {
    if (allModules.length === 0) {
      return
    }
    const selectedIds = selectedModules?.map((m) => m.apprenticeship_module_id)

    // only use teh active modules and then only use the modules which are not already selected
    const allModulesExceptSelectedModules: ApprenticeshipModuleInPlan[] =
      allModules
        .filter((y) => y.is_active === 1)
        .filter(
          (x: ApprenticeshipModule) =>
            !selectedIds?.includes(x.apprenticeship_module_id)
        ) as ApprenticeshipModuleInPlan[]

    const sortedModules = sortByKey(
      'module_key',
      allModulesExceptSelectedModules
    )
    setAllModulesWithoutSelected(sortedModules)
  }, [allModules, selectedModules])

  const onPicklistChange = (e: any) => {
    setAllModulesWithoutSelected(e.source)
    setSelectedModules(e.target)
  }

  const toggleSemesterTree = () => {
    if (!isExpanded) {
      expandAll()
    } else {
      collapseAll()
    }
    // update the state
    setIsExpanded(!isExpanded)
  }

  const setupSemesterNodes = () => {
    const tempNodeArray: TreeNode[] = []
    if (!apprenticeshipDurationInSemester) {
      setSemesterNodes(tempNodeArray)
      return
    }
    for (let i = 1; i <= apprenticeshipDurationInSemester; i++) {
      tempNodeArray.push({
        key: i,
        label: `. ${t<string>('core.education.semester')}`,
        draggable: false,
        icon: `fa-solid fa-${i}`,
        children: [],
        className: 'parent-tree-node',
      })
    }

    selectedModules?.forEach(
      (moduleWithSemester: ApprenticeshipModuleWithSemesters) => {
        moduleWithSemester?.semesters?.forEach((semNr: number) => {
          const tempNode: TreeNode = {
            key: `${semNr}_${moduleWithSemester.apprenticeship_module_id}`,
            //icon: getIcon(moduleWithSemester.apprenticeshipModule.id_apprenticeship_module_type),
            //label: `${moduleWithSemester.apprenticeshipModule.module_key} -
            //${moduleWithSemester.apprenticeshipModule.description}`,
            draggable: false,
            leaf: true,
            droppable: false,
            data: { ...moduleWithSemester },
            selectable: false,
          }
          const childRef = tempNodeArray[semNr - 1]?.children
          // only add the key once
          if (
            childRef &&
            !childRef.find(
              (x: TreeNode) =>
                x.key ===
                `${semNr}_${moduleWithSemester.apprenticeship_module_id}`
            )
          ) {
            tempNodeArray[semNr - 1]?.children?.push(tempNode)
          }
        })
      }
    )
    setSemesterNodes(tempNodeArray)
  }

  const formMethods = useForm<ApprenticeshipModulePlan>()
  const onSubmit: SubmitHandler<ApprenticeshipModulePlan> = async (data) => {
    console.log(
      '[Add Apprenticeship Module Plan] Add button pressed. Preparing data.'
    )

    const appModules: {
      apprenticeship_module_id: number
      semester: number
      id_grade_schema: number
      id_rounding_type: number
    }[] = []

    const distinctModulesSet = new Set(selectedModules)

    // do not add semesters which are after the duration, this happens,
    // when the type is changed after selecting semesters, we do not want
    // to remove them in case the type is changed again before saving
    const maxAllowedSemesterNumber = apprenticeshipDurationInSemester

    // add new modules
    distinctModulesSet.forEach((module) => {
      module?.semesters?.forEach((sem: number) => {
        if (
          module?.apprenticeship_module_id &&
          maxAllowedSemesterNumber &&
          sem <= maxAllowedSemesterNumber
        ) {
          appModules.push({
            apprenticeship_module_id: module.apprenticeship_module_id,
            semester: sem,
            id_grade_schema: module.id_grade_schema,
            id_rounding_type: module.id_rounding_type,
          })
        }
      })
    })

    const body = {
      description: data.description,
      id_apprenticeship_type: data.id_apprenticeship_type,
      professions: data.professions?.map((prof) => {
        return { profession_id: prof.profession_id }
      }),
      specialities: data.specialities?.map((spec) => {
        return { speciality_id: spec.speciality_id }
      }),
      groups: data.groups?.map((group) => {
        return { group_id: group.group_id }
      }),
      apprenticeship_modules: appModules,
    } as ApprenticeshipModulePlan

    const result = await handleRequest<ApprenticeshipModulePlan>(
      API_APPRENTICESHIP_MODULE_PLANS(),
      RequestMethod.POST,
      body
    )

    if (result?.data?.apprenticeship_module_plan_id && !result.error) {
      console.log(
        '[Add Apprenticeship Module Plan] Successfully created the module plan.'
      )

      navigate(
        `${AuthRoutes.educationPlans}/${result.data.apprenticeship_module_plan_id}`
      )
      return
    }
    console.warn(
      '[Add Apprenticeship Module Plan] Could not create the module plan.',
      result.error
    )
  }

  const apprenticeTypeOptions: DropdownItem[] = [
    {
      label: t<string>('core.apprenticeship_type.eba'),
      value: ApprenticeshipTypeCt.eba,
    },
    {
      label: t<string>('core.apprenticeship_type.efz'),
      value: ApprenticeshipTypeCt.efz,
    },
    {
      label: t<string>('core.apprenticeship_type.ims'),
      value: ApprenticeshipTypeCt.ims,
    },
    {
      label: t<string>('core.apprenticeship_type.way_up'),
      value: ApprenticeshipTypeCt.way_up,
    },
  ]

  const setSemesterDurationByApprenticeshipType = async (typeId: number) => {
    let types = apprenticeshipTypes
    if (types.length <= 0) {
      const result = await handleRequest<ApprenticeshipType[]>(
        API_APPRENTICESHIP_TYPES(),
        RequestMethod.GET
      )
      if (result.data && !result.error) {
        setApprenticeshipTypes(result.data)
        types = result.data
      }
    }
    const selectedType = types.find(
      (at) => at.apprenticeship_type_id === typeId
    )
    if (selectedType) {
      const durationInSem = selectedType.duration_in_months / 6
      setApprenticeshipDurationInSemester(durationInSem)
      const sems = getSemesterArray(durationInSem)
      setSemesterSelectOptions(sems)
    }
  }

  return (
    <div className={'gap-1 pr-2'}>
      <BackButton translationKey={'navigation.go_back_overview'} />
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Card className={'mb-2'}>
            <div className={'col gap-1'}>
              <div className={'col-12 flex p-fluid '}>
                <label className={'col'}>
                  {t<string>('grade_module.education_plan.plan_description')}
                </label>
                <Controller
                  name={'description'}
                  control={formMethods.control}
                  defaultValue={''}
                  rules={{ required: true, maxLength: 200 }}
                  render={({ field }) => (
                    <InputText {...field} className={'col'} />
                  )}
                />
              </div>
              <div className={'col-12 flex p-fluid gap-1'}>
                <label className={'col'}>
                  {t<string>('core.apprenticeship_type.title')}
                </label>
                <Controller
                  name={'id_apprenticeship_type'}
                  control={formMethods.control}
                  rules={{ required: true }}
                  defaultValue={ApprenticeshipTypeCt.efz}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      name={field.name}
                      value={field.value}
                      options={apprenticeTypeOptions}
                      onChange={(e) => {
                        field.onChange(e.target.value)
                        setSemesterDurationByApprenticeshipType(e.target.value)
                      }}
                      className={'col'}
                    />
                  )}
                />
              </div>

              <div
                className={'col-12 flex p-fluid flex-wrap inline-flex w-full'}
              >
                <div className={'col inline-flex'}>
                  <label>
                    {t<string>('grade_module.education_plan.groups_in_plan')}
                  </label>
                  <div className={'align-items-end justify-content-end mr-0'}>
                    <Button
                      type={'button'}
                      icon={'fa-solid fa-users pt-1'}
                      tooltip={
                        t<string>(
                          'grade_module.education_plan.route_to_groups'
                        ) +
                        ': ' +
                        t<string>('core.unsaved_data_will_be_lost')
                      }
                      onClick={() => navigate(AuthRoutes.groups)}
                      className={'p-button-help ml-2 small-text'}
                      style={{ width: '24px', height: '24px' }}
                    />
                  </div>
                </div>
                <GroupMultiselect />
              </div>
            </div>
          </Card>

          <Steps
            className={'mt-4 mb-4'}
            model={stepItems}
            activeIndex={activeIndex}
            onSelect={(e) => {
              setupSemesterNodes()
              setActiveIndex(e.index)
            }}
            readOnly={selectedModules.length === 0}
          />
          {activeIndex === 0 && (
            <>
              <div style={{ maxHeight: '58vh', overflow: 'auto' }}>
                <PickList
                  className={'module-picklist'}
                  source={sortByKey(
                    'id_apprenticeship_module_type',
                    allModulesWithoutSelected
                  )}
                  target={sortByKey(
                    'id_apprenticeship_module_type',
                    selectedModules
                  )}
                  showSourceFilter={true}
                  showTargetFilter={true}
                  filterBy={'module_key,description'}
                  sourceFilterPlaceholder={t<string>('core.filter')}
                  targetFilterPlaceholder={t<string>('core.filter')}
                  filterMatchMode={FilterMatchMode.CONTAINS}
                  onChange={onPicklistChange}
                  sourceItemTemplate={(e) => (
                    <ModulePlanItemTemplate module={e} />
                  )}
                  targetItemTemplate={(
                    module: ApprenticeshipModuleWithSemesters
                  ) => (
                    <Card
                      className={classNames(
                        { 'inactive-card': module.is_active === 0 },
                        '  '
                      )}
                      key={module.apprenticeship_module_id}
                    >
                      <div
                        className={
                          'w-full align-items-center inline-flex flex-wrap'
                        }
                      >
                        <div className={'p-0 m-0 col-1 mr-1 ml-2'}>
                          <ModuleKeyBadgeByType
                            moduleKey={module.module_key}
                            moduleType={module.id_apprenticeship_module_type}
                          />
                        </div>
                        <div className={'col'}>
                          <b>{module.description}</b>
                        </div>
                        <div className={'p-0 m-0 col-4 inline-flex flex-wrap'}>
                          {module.id_apprenticeship_module_type && (
                            <div className={'p-0 m-0 col-12 '}>
                              <ModuleTypeTag
                                moduleType={
                                  module.id_apprenticeship_module_type
                                }
                              />
                            </div>
                          )}
                          <div className={'col-12 p-0 m-0 '}>
                            <ModuleTags module={module} />
                          </div>
                        </div>
                        <div
                          className={
                            'col-12 p-0 m-0 inline-flex align-items-center justify-content-end w-full'
                          }
                          style={{ zIndex: 100 }}
                        >
                          <span className={'col small-text'}>
                            {t<string>('grade_module.grade.schema.name')}:{' '}
                          </span>
                          <div className={'col mr-0 pr-0'}>
                            <GradeSchemaDropdown
                              selectedId={
                                module.id_grade_schema
                                  ? module.id_grade_schema
                                  : 2 // CH as default value
                              }
                              setSelected={(e: GradeSchema) => {
                                if (!e) {
                                  return
                                }
                                const modules = [...selectedModules]
                                const l = modules.find(
                                  (m) =>
                                    m.apprenticeship_module_id ===
                                    module.apprenticeship_module_id
                                )
                                if (l) {
                                  l.id_grade_schema = e.grade_schema_id
                                  setSelectedModules(modules)
                                }
                              }}
                            />
                          </div>
                        </div>
                        <div
                          className={
                            'col-12 p-0 m-0 inline-flex align-items-center justify-content-end w-full'
                          }
                          style={{ zIndex: 100 }}
                        >
                          {module.id_grade_schema !== 5 ? (
                            <>
                              <span className={'col small-text'}>
                                {t<string>(
                                  'grade_module.grade.rounding_type.name'
                                )}
                                :{' '}
                              </span>
                              <div className={'col mr-0 pr-0'}>
                                <RoundingTypeDropdown
                                  activeValue={
                                    module.id_rounding_type
                                      ? module.id_rounding_type
                                      : 3 // 3 = up to next half
                                  }
                                  setActive={(v: number) => {
                                    module.id_rounding_type = v
                                  }}
                                  fieldIsDisabled={module.id_grade_schema === 5}
                                />
                              </div>
                            </>
                          ) : (
                            <div className={'col-8 mr-0 pr-0'} />
                          )}
                        </div>
                        <div
                          className={
                            'col-12 p-0 m-0 inline-flex align-items-center justify-content-end w-full'
                          }
                        >
                          <span className={'col small-text'}>
                            {t<string>('core.education.semester')}:
                          </span>
                          <div
                            className={
                              'col inline-flex justify-content-end mr-0 pr-0'
                            }
                          >
                            <SelectButton
                              key={'select_' + module.apprenticeship_module_id}
                              value={
                                selectedModules.find(
                                  (sm) =>
                                    sm.apprenticeship_module_id ===
                                    module.apprenticeship_module_id
                                )?.semesters
                              }
                              type={'button'}
                              options={semesterSelectOptions}
                              multiple
                              onChange={(e) => {
                                //console.log(e.target.value)

                                const modules = [...selectedModules]
                                const l = modules.find(
                                  (m) =>
                                    m.apprenticeship_module_id ===
                                    module.apprenticeship_module_id
                                )
                                //console.log(e.target.value)
                                if (l) {
                                  l.semesters = e.target.value
                                  setSelectedModules(modules)
                                }
                              }}
                            />
                            <div
                              className={
                                'col-2 inline-flex p-0 m-0 ml-2 justify-content-end'
                              }
                            >
                              <Button
                                type={'button'}
                                style={{ zIndex: 100 }}
                                icon={'fa-solid fa-square-check fa-xl'}
                                tooltip={t<string>('core.education.select_all')}
                                tooltipOptions={{
                                  position: 'left',
                                  showDelay: 800,
                                }}
                                className={'p-button-text p-0 m-0'}
                                onClick={() => {
                                  const modules = [...selectedModules]
                                  const l = modules.find(
                                    (m) =>
                                      m.apprenticeship_module_id ===
                                      module.apprenticeship_module_id
                                  )
                                  if (l) {
                                    l.semesters = getSemesterArray(
                                      apprenticeshipDurationInSemester
                                    )
                                  }
                                  setSelectedModules(modules)
                                }}
                              />
                              <Button
                                tooltip={t<string>(
                                  'core.education.unselect_all'
                                )}
                                tooltipOptions={{
                                  position: 'left',
                                  showDelay: 800,
                                }}
                                icon={'fa-solid fa-square-xmark fa-xl'}
                                style={{ zIndex: 100 }}
                                type={'button'}
                                className={
                                  'p-button-text p-button-secondary p-0 m-0'
                                }
                                onClick={(e) => {
                                  const modules = selectedModules
                                  const l = modules.find(
                                    (m) =>
                                      m.apprenticeship_module_id ===
                                      module.apprenticeship_module_id
                                  )
                                  // console.log(l)
                                  if (l) {
                                    l.semesters = []
                                  }
                                  setSelectedModules(modules)
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}
                  showSourceControls={false}
                  showTargetControls={false}
                  sourceHeader={t<string>('grade_module.module.all_modules')}
                  targetHeader={t<string>(
                    'grade_module.module.selected_modules'
                  )}
                  metaKeySelection={true}
                />
                <Card
                  className={
                    'p-inline-message p-inline-message-info p-2 mt-2 mb-2 mr-2'
                  }
                >
                  <b>
                    <span
                      className={'p-inline-message-icon pi pi-info-circle'}
                    />
                    {t<string>(
                      'grade_module.education_plan.explanation.summary'
                    )}
                  </b>
                  <p className={'small-text'}>
                    {t<string>(
                      'grade_module.education_plan.explanation.detail'
                    )}
                  </p>
                </Card>
              </div>
              <div className={'w-full flex mt-2 mb-2'}>
                <Button
                  className={'ml-auto'}
                  type={'button'}
                  onClick={() => setActiveIndex(1)}
                  label={t<string>('core.review_result')}
                />
              </div>
            </>
          )}
          {activeIndex === 1 && (
            <>
              <div className={'w-full'}>
                <div className="mb-2 ">
                  <ToggleButton
                    type="button"
                    checked={isExpanded}
                    onIcon="fa-solid fa-minus"
                    onLabel={t<string>('core.collapse_all')}
                    offIcon="fa-solid fa-chevron-right"
                    offLabel={t<string>('core.show_all')}
                    onChange={toggleSemesterTree}
                    className=" p-button-text p-button-raised p-button-rounded min-width-220"
                  />
                </div>
                <Tree
                  style={{
                    height: '56vh',
                    maxHeight: '56vh',
                    overflow: 'auto',
                  }}
                  value={semesterNodes}
                  expandedKeys={expandedKeys}
                  className={'semester-tree'}
                  nodeTemplate={ModuleNodeTemplate}
                  onToggle={(e) => setExpandedKeys(e.value)}
                />

                <div className={'w-full flex mt-2 mb-2'}>
                  <Button
                    className={'ml-auto p-button-outlined'}
                    type={'button'}
                    label={t<string>(
                      'grade_module.education_plan.revise_modules'
                    )}
                    onClick={() => setActiveIndex(0)}
                  />
                  <Button
                    className={'ml-2'}
                    type={'submit'}
                    label={t<string>('grade_module.education_plan.create')}
                  />
                </div>
              </div>
            </>
          )}
        </form>
      </FormProvider>
    </div>
  )
}